import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchUserMe,
  loginUser,
  logoutUser,
  registerUser,
  startSoftIdentity,
  updateUserProfile,
  verifyRecoveryCode,
} from "./authApi";
import JourneyService from "../services/JourneyService";
import { getDeviceId } from "../lib/deviceId";

const AuthContext = createContext(null);
const LAST_AUTH_USER_KEY = "innervoice_last_auth_user_id";

function readLastAuthUserId() {
  try {
    return localStorage.getItem(LAST_AUTH_USER_KEY) || "";
  } catch {
    return "";
  }
}

function writeLastAuthUserId(id) {
  try {
    if (id) localStorage.setItem(LAST_AUTH_USER_KEY, id);
    else localStorage.removeItem(LAST_AUTH_USER_KEY);
  } catch {
    /* ignore */
  }
}

/** כשנכנס משתמש (או משתנה מזהה), ננקה מטמון מסע מקומי כדי שלא יישארו מפתחות בלי userId מהחשבון הקודם */
function reconcileJourneyCacheForUser(nextUser) {
  const nextId = nextUser?.id != null ? String(nextUser.id) : "";
  const prevId = readLastAuthUserId();
  if (prevId !== nextId) {
    JourneyService.clearLocalJourneyCache();
  }
  writeLastAuthUserId(nextId);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const softIdentityAttemptedRef = useRef(false);

  const refresh = useCallback((opts = {}) => {
    const silent = Boolean(opts.silent);
    if (silent) {
      return fetchUserMe()
        .then((data) => {
          const nextUser = data.user || null;
          setUser(nextUser);
          if (nextUser) {
            JourneyService.syncFromServer().catch(() => {});
          }
          return nextUser;
        })
        .catch(() => null);
    }
    setLoading(true);
    return fetchUserMe()
      .then((data) => {
        const nextUser = data.user || null;
        setUser(nextUser);
        if (nextUser) {
          JourneyService.syncFromServer().catch(() => {});
        }
        return nextUser;
      })
      .catch(async () => {
        if (softIdentityAttemptedRef.current) {
          setUser(null);
          return null;
        }
        softIdentityAttemptedRef.current = true;
        try {
          const deviceId = getDeviceId();
          const data = await startSoftIdentity({ deviceId });
          const nextUser = data.user || null;
          setUser(nextUser);
          if (nextUser) {
            JourneyService.syncFromServer().catch(() => {});
          }
          return nextUser;
        } catch {
          setUser(null);
          return null;
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (payload) => {
    const data = await loginUser(payload);
    const nextUser = data.user || null;
    setUser(nextUser);
    if (nextUser) {
      reconcileJourneyCacheForUser(nextUser);
      JourneyService.syncFromServer().catch(() => {});
    }
    return nextUser;
  };

  const loginWithCode = async (payload) => {
    const data = await verifyRecoveryCode(payload);
    const nextUser = data.user || null;
    setUser(nextUser);
    if (nextUser) {
      reconcileJourneyCacheForUser(nextUser);
      JourneyService.syncFromServer().catch(() => {});
    }
    return nextUser;
  };

  const register = async (payload) => {
    const data = await registerUser(payload);
    const nextUser = data.user || null;
    setUser(nextUser);
    if (nextUser) {
      reconcileJourneyCacheForUser(nextUser);
      JourneyService.syncFromServer().catch(() => {});
    }
    return nextUser;
  };

  const updateProfile = async (payload) => {
    const data = await updateUserProfile(payload);
    const nextUser = data.user || null;
    setUser(nextUser);
    return nextUser;
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch {
      // Best-effort logout: clear local state even if the request fails.
    }
    reconcileJourneyCacheForUser(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      loginWithCode,
      register,
      updateProfile,
      logout,
      refresh,
    }),
    [user, loading, refresh, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
